import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core'
import { FileCopy } from '@material-ui/icons'
import { useQuery } from 'react-query'
import { useApi } from '../ApiContext'
import { useProject } from '../ProjectWrapper'
import { Stream } from '../../api/codegen/typescript-axios'

export const StreamEmbedTestPage = () => {
  const api = useApi()
  const project = useProject()

  // Form states
  const [selectedStream, setSelectedStream] = useState<Stream | null>(null)
  const [width, setWidth] = useState(800)
  const [height, setHeight] = useState(450)
  const [isResponsive, setIsResponsive] = useState(true)

  // Helper function to parse aspect ratio and calculate dimensions
  const calculateDimensions = (baseWidth: number, aspectRatio?: string) => {
    if (!aspectRatio) {
      // Default to 16:9 if no aspect ratio available
      return { width: baseWidth, height: Math.round(baseWidth * (9 / 16)) }
    }

    const [widthRatio, heightRatio] = aspectRatio.split(':').map(Number)
    if (widthRatio && heightRatio) {
      return {
        width: baseWidth,
        height: Math.round(baseWidth * (heightRatio / widthRatio)),
      }
    }

    // Fallback to 16:9
    return { width: baseWidth, height: Math.round(baseWidth * (9 / 16)) }
  }

  // Helper function to calculate padding-bottom percentage for responsive aspect ratio
  const getAspectRatioPadding = (aspectRatio?: string) => {
    if (!aspectRatio) {
      return '56.25%' // 16:9 default
    }

    const [widthRatio, heightRatio] = aspectRatio.split(':').map(Number)
    if (widthRatio && heightRatio) {
      return `${(heightRatio / widthRatio) * 100}%`
    }

    return '56.25%' // 16:9 fallback
  }

  // Fetch project details to get full stream data including aspect_ratio
  const { data: projectDetails, isLoading: isLoadingProjectDetails } = useQuery(
    ['projectDetails', project.id],
    async () => {
      const resp = await api.projectsApi.projectsRead({
        id: project.id.toString(),
      })
      return resp.data
    },
    {
      enabled: !!project.id,
    }
  )

  const availableStreams = projectDetails?.streams || []

  // Auto-select first stream when streams become available
  useEffect(() => {
    if (
      projectDetails?.streams &&
      projectDetails.streams.length > 0 &&
      !selectedStream
    ) {
      setSelectedStream(projectDetails.streams[0])
    }
  }, [projectDetails?.streams, selectedStream])

  const src = selectedStream
    ? `${window.location.origin}${process.env.PUBLIC_URL}/public/${project.slug}/live/stream/${selectedStream.source_stream_name}`
    : ''

  // Generate iframe code
  const iframeCode = selectedStream
    ? isResponsive
      ? `<div style="position: relative; width: 100%; height: 0; padding-bottom: ${getAspectRatioPadding(
          selectedStream.aspect_ratio
        )}; overflow: hidden;">
  <iframe 
    src="${src}"
    allowfullscreen
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;"
  ></iframe>
</div>`
      : `<iframe 
  src="${src}" 
  width="${width}" 
  height="${height}"
  style="display: block; border: none;"
  allowfullscreen
></iframe>`
    : ''

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Stream Embed Code Generator
      </Typography>

      <Grid container spacing={3}>
        {/* Configuration Panel */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configuration
              </Typography>

              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Project: {project.name}
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {/* Stream Selection */}
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Stream
                  </Typography>
                  {isLoadingProjectDetails ? (
                    <Typography>Loading streams...</Typography>
                  ) : (
                    <Select
                      fullWidth
                      value={selectedStream?.source_stream_name || ''}
                      onChange={(e) => {
                        const stream = availableStreams.find(
                          (s) => s.source_stream_name === e.target.value
                        )
                        setSelectedStream(stream || null)
                      }}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Select a stream</em>
                      </MenuItem>
                      {availableStreams.map((stream) => (
                        <MenuItem
                          key={stream.source_stream_name}
                          value={stream.source_stream_name}
                        >
                          {stream.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </Grid>

                {/* Preset Size Buttons */}
                {selectedStream && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Preset Sizes (Aspect Ratio:{' '}
                      {selectedStream.aspect_ratio || '16:9'})
                    </Typography>
                    <Box>
                      <Button
                        size="small"
                        variant={isResponsive ? 'contained' : 'outlined'}
                        color={isResponsive ? 'primary' : 'default'}
                        onClick={() => {
                          setIsResponsive(true)
                          // Set default dimensions for preview (responsive will override in actual code)
                          const dims = calculateDimensions(
                            800,
                            selectedStream.aspect_ratio
                          )
                          setWidth(dims.width)
                          setHeight(dims.height)
                        }}
                        style={{ marginRight: 8, marginBottom: 8 }}
                      >
                        Responsive (Fluid)
                      </Button>
                      <Button
                        size="small"
                        variant={!isResponsive ? 'outlined' : 'outlined'}
                        onClick={() => {
                          setIsResponsive(false)
                          const dims = calculateDimensions(
                            1280,
                            selectedStream.aspect_ratio
                          )
                          setWidth(dims.width)
                          setHeight(dims.height)
                        }}
                        style={{ marginRight: 8, marginBottom: 8 }}
                      >
                        Large (1280px)
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setIsResponsive(false)
                          const dims = calculateDimensions(
                            800,
                            selectedStream.aspect_ratio
                          )
                          setWidth(dims.width)
                          setHeight(dims.height)
                        }}
                        style={{ marginRight: 8, marginBottom: 8 }}
                      >
                        Medium (800px)
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setIsResponsive(false)
                          const dims = calculateDimensions(
                            640,
                            selectedStream.aspect_ratio
                          )
                          setWidth(dims.width)
                          setHeight(dims.height)
                        }}
                        style={{ marginRight: 8, marginBottom: 8 }}
                      >
                        Small (640px)
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setIsResponsive(false)
                          const dims = calculateDimensions(
                            400,
                            selectedStream.aspect_ratio
                          )
                          setWidth(dims.width)
                          setHeight(dims.height)
                        }}
                        style={{ marginBottom: 8 }}
                      >
                        Tiny (400px)
                      </Button>
                    </Box>
                  </Grid>
                )}

                {/* Width */}
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Width {isResponsive && '(Disabled in Responsive Mode)'}
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={width}
                    onChange={(e) => setWidth(Number(e.target.value))}
                    disabled={isResponsive}
                  />
                </Grid>

                {/* Height */}
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Height {isResponsive && '(Disabled in Responsive Mode)'}
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(Number(e.target.value))}
                    disabled={isResponsive}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Live Preview */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Live Preview
              </Typography>
              {selectedStream ? (
                <div>
                  {isResponsive ? (
                    <div
                      style={{
                        position: 'relative',
                        width: '100%',
                        height: 0,
                        paddingBottom: getAspectRatioPadding(
                          selectedStream.aspect_ratio
                        ),
                        overflow: 'hidden',
                      }}
                    >
                      <iframe
                        src={src}
                        allowFullScreen
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          border: 'none',
                        }}
                      />
                    </div>
                  ) : (
                    <iframe
                      src={src}
                      width={width}
                      height={height}
                      allowFullScreen
                      style={{ border: 'none', display: 'block' }}
                    />
                  )}
                </div>
              ) : (
                <Typography color="textSecondary">
                  Select a stream to see the preview
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Generated Code */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Typography variant="h6">Generated Iframe Code</Typography>
                {iframeCode && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(iframeCode)
                    }}
                    style={{ marginLeft: 8 }}
                  >
                    <FileCopy />
                  </IconButton>
                )}
              </Box>
              {iframeCode ? (
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  value={iframeCode}
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                  }}
                  onClick={(e) => {
                    const target = e.target as HTMLTextAreaElement
                    if (target && typeof target.select === 'function') {
                      target.select()
                    }
                  }}
                />
              ) : (
                <Typography color="textSecondary">
                  Select a stream to generate iframe code
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
